/**
 * MANAGEMENT (14) / Support — the Support SCREEN's i18n strings (FR default + AR-RTL), registered
 * ADDITIVELY in a NEW idempotent namespace (the shell's messages.ts already owns the nav + section
 * title/subtitle; this file adds the inbox/triage/chat strings the screen needs). Registered once from
 * the SupportPage on mount, so it works whether or not the shell's onRegister already ran. Missing AR key
 * → FR value → raw key (never a crash, never a blank).
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // ── inbox / triage ──────────────────────────────────────────────────────────────
  "mgmt.support.inbox.title": "Tickets de support",
  "mgmt.support.inbox.search": "Rechercher (sujet, référence)",
  "mgmt.support.inbox.empty.title": "Aucun ticket",
  "mgmt.support.inbox.empty.desc": "Aucun ticket ne correspond à ces filtres.",
  "mgmt.support.col.reference": "Référence",
  "mgmt.support.col.sujet": "Sujet",
  "mgmt.support.col.statut": "Statut",
  "mgmt.support.col.priorite": "Priorité",
  "mgmt.support.col.categorie": "Catégorie",
  "mgmt.support.col.unread": "Non lus",
  "mgmt.support.filter.all": "Tous",
  "mgmt.support.filter.statut": "Filtrer par statut",
  "mgmt.support.filter.priorite": "Filtrer par priorité",
  "mgmt.support.open": "Ouvrir le ticket {ref}",
  "mgmt.support.unread.aria": "{n} message(s) non lu(s)",

  // ── insight strip (derived from the list envelope — parStatut/ouverts + unread) ─
  "mgmt.support.insight.title": "Synthèse du support (cross-lab)",
  "mgmt.support.insight.byStatut": "Par statut",
  "mgmt.support.insight.byStatut.aria": "Répartition des tickets par statut",
  "mgmt.support.insight.empty": "Aucun ticket de support à analyser.",
  "mgmt.support.insight.open": "À traiter",
  "mgmt.support.insight.total": "Total tickets",
  "mgmt.support.insight.unread": "Réponses non lues",

  // ── statuts ────────────────────────────────────────────────────────────────────
  "mgmt.support.statut.ouvert": "Ouvert",
  "mgmt.support.statut.en_cours": "En cours",
  "mgmt.support.statut.resolu": "Résolu",
  "mgmt.support.statut.ferme": "Clôturé",

  // ── priorités ──────────────────────────────────────────────────────────────────
  "mgmt.support.priorite.basse": "Basse",
  "mgmt.support.priorite.normale": "Normale",
  "mgmt.support.priorite.haute": "Haute",
  "mgmt.support.priorite.urgente": "Urgente",

  // ── catégories ─────────────────────────────────────────────────────────────────
  "mgmt.support.categorie.facturation": "Facturation",
  "mgmt.support.categorie.technique": "Technique",
  "mgmt.support.categorie.donnees": "Données",
  "mgmt.support.categorie.compte": "Compte",
  "mgmt.support.categorie.autre": "Autre",

  // ── chat panel ──────────────────────────────────────────────────────────────────
  "mgmt.support.chat.title": "Conversation",
  "mgmt.support.chat.empty.title": "Sélectionnez un ticket",
  "mgmt.support.chat.empty.desc": "Choisissez un ticket dans la liste pour ouvrir le fil de discussion.",
  "mgmt.support.chat.thread.empty": "Aucun message pour l'instant.",
  "mgmt.support.chat.author.admin": "Vous (plateforme)",
  "mgmt.support.chat.author.labo": "Laboratoire",
  "mgmt.support.chat.live": "En direct",
  "mgmt.support.chat.unread.banner": "{n} nouveau(x) message(s) du laboratoire",
  "mgmt.support.chat.markread": "Marquer comme lu",
  "mgmt.support.chat.closed": "Ticket clôturé — rouvrez-le pour répondre.",

  // ── composer ────────────────────────────────────────────────────────────────────
  "mgmt.support.composer.label": "Votre réponse",
  "mgmt.support.composer.placeholder": "Écrivez une réponse…",
  "mgmt.support.composer.send": "Envoyer",
  "mgmt.support.composer.sending": "Envoi…",
  "mgmt.support.composer.error": "L'envoi a échoué. Réessayez.",
  "mgmt.support.composer.empty": "Le message ne peut pas être vide.",

  // ── triage actions ──────────────────────────────────────────────────────────────
  "mgmt.support.triage.statut": "Changer le statut",
  "mgmt.support.triage.saved": "Ticket mis à jour.",
  "mgmt.support.triage.conflict": "Le ticket a changé entre-temps — rechargez puis réessayez.",
  "mgmt.support.sent": "Message envoyé.",
  "mgmt.support.read.done": "Conversation marquée comme lue.",
};

const AR: Record<string, string> = {
  "mgmt.support.inbox.title": "تذاكر الدعم",
  "mgmt.support.inbox.search": "بحث (الموضوع، المرجع)",
  "mgmt.support.inbox.empty.title": "لا توجد تذاكر",
  "mgmt.support.inbox.empty.desc": "لا توجد تذاكر مطابقة لهذه المرشحات.",
  "mgmt.support.col.reference": "المرجع",
  "mgmt.support.col.sujet": "الموضوع",
  "mgmt.support.col.statut": "الحالة",
  "mgmt.support.col.priorite": "الأولوية",
  "mgmt.support.col.categorie": "الفئة",
  "mgmt.support.col.unread": "غير مقروء",
  "mgmt.support.filter.all": "الكل",
  "mgmt.support.filter.statut": "تصفية حسب الحالة",
  "mgmt.support.filter.priorite": "تصفية حسب الأولوية",
  "mgmt.support.open": "فتح التذكرة {ref}",
  "mgmt.support.unread.aria": "{n} رسالة غير مقروءة",

  "mgmt.support.insight.title": "ملخص الدعم (عبر المختبرات)",
  "mgmt.support.insight.byStatut": "حسب الحالة",
  "mgmt.support.insight.byStatut.aria": "توزيع التذاكر حسب الحالة",
  "mgmt.support.insight.empty": "لا توجد تذاكر دعم للتحليل.",
  "mgmt.support.insight.open": "قيد المعالجة",
  "mgmt.support.insight.total": "إجمالي التذاكر",
  "mgmt.support.insight.unread": "الردود غير المقروءة",

  "mgmt.support.statut.ouvert": "مفتوحة",
  "mgmt.support.statut.en_cours": "قيد المعالجة",
  "mgmt.support.statut.resolu": "محلولة",
  "mgmt.support.statut.ferme": "مغلقة",

  "mgmt.support.priorite.basse": "منخفضة",
  "mgmt.support.priorite.normale": "عادية",
  "mgmt.support.priorite.haute": "عالية",
  "mgmt.support.priorite.urgente": "عاجلة",

  "mgmt.support.categorie.facturation": "الفوترة",
  "mgmt.support.categorie.technique": "تقني",
  "mgmt.support.categorie.donnees": "البيانات",
  "mgmt.support.categorie.compte": "الحساب",
  "mgmt.support.categorie.autre": "أخرى",

  "mgmt.support.chat.title": "المحادثة",
  "mgmt.support.chat.empty.title": "اختر تذكرة",
  "mgmt.support.chat.empty.desc": "اختر تذكرة من القائمة لفتح سلسلة المحادثة.",
  "mgmt.support.chat.thread.empty": "لا توجد رسائل بعد.",
  "mgmt.support.chat.author.admin": "أنت (المنصة)",
  "mgmt.support.chat.author.labo": "المختبر",
  "mgmt.support.chat.live": "مباشر",
  "mgmt.support.chat.unread.banner": "{n} رسالة جديدة من المختبر",
  "mgmt.support.chat.markread": "وضع علامة مقروء",
  "mgmt.support.chat.closed": "التذكرة مغلقة — أعد فتحها للرد.",

  "mgmt.support.composer.label": "ردك",
  "mgmt.support.composer.placeholder": "اكتب ردًا…",
  "mgmt.support.composer.send": "إرسال",
  "mgmt.support.composer.sending": "جارٍ الإرسال…",
  "mgmt.support.composer.error": "فشل الإرسال. أعد المحاولة.",
  "mgmt.support.composer.empty": "لا يمكن أن تكون الرسالة فارغة.",

  "mgmt.support.triage.statut": "تغيير الحالة",
  "mgmt.support.triage.saved": "تم تحديث التذكرة.",
  "mgmt.support.triage.conflict": "تغيّرت التذكرة في هذه الأثناء — أعد التحميل ثم حاول مجددًا.",
  "mgmt.support.sent": "تم إرسال الرسالة.",
  "mgmt.support.read.done": "تم وضع علامة مقروء على المحادثة.",
};

let registered = false;
export function registerSupportMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
